// SearchPage — Global search across portal
// Searches: pages, trades, bots, news, articles
// Layout: search bar with focus, recent + results

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Result {
  id: string;
  type: 'page' | 'trade' | 'article' | 'bot' | 'news';
  title: string;
  detail: string;
  to: string;
  icon: string;
  color: string;
}

const MOCK_RESULTS: Result[] = [
  { id: 'r1', type: 'page',   title: 'Analytics',           detail: 'P&L, win rate, risk metrics',       to: '/portal/analytics',  icon: '📊', color: '#007AFF' },
  { id: 'r2', type: 'page',   title: 'Goals',               detail: 'Set and track trading goals',        to: '/portal/goals',      icon: '🎯', color: '#FF3B30' },
  { id: 'r3', type: 'page',   title: 'AI Bot Trader',       detail: 'Manage strategies and bots',        to: '/portal/bot',        icon: '🤖', color: '#34C759' },
  { id: 'r4', type: 'trade',  title: 'NQ long +$895',       detail: 'Trade closed 2h 14m ago',             to: '/portal/trade/t1',   icon: '📈', color: '#34C759' },
  { id: 'r5', type: 'article', title: 'How daily drawdown works', detail: 'Help center · 4 min read',         to: '/portal/article/drawdown', icon: '📉', color: '#FF9500' },
  { id: 'r6', type: 'bot',    title: 'Breakout strategy',   detail: 'Currently running · +$184.50',       to: '/portal/strategy/breakout', icon: '🚀', color: '#34C759' },
  { id: 'r7', type: 'news',   title: 'Fed Chair Powell speaks today', detail: 'Market event at 14:00 ET',      to: '/portal/market-calendar', icon: '📰', color: '#FF3B30' },
];

const RECENT = [
  { q: 'AI Bot', time: '5 min ago' },
  { q: 'KYC',    time: '2 days ago' },
  { q: 'Payouts',time: '1 week ago' },
];

const SUGGESTED = ['How to withdraw', 'API keys', 'Daily drawdown', 'Profit split', 'Challenge rules'];

export function SearchPage() {
  const nav = useNavigate();
  const [q, setQ] = useState('');

  const filtered = q ? MOCK_RESULTS.filter((r) => r.title.toLowerCase().includes(q.toLowerCase()) || r.detail.toLowerCase().includes(q.toLowerCase())) : MOCK_RESULTS.slice(0, 5);

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl gap-2">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="flex-1 relative">
          <svg viewBox="0 0 24 24" className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-tf-text-3" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" strokeLinecap="round" /></svg>
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search portal, trades, articles..."
            autoFocus
            data-testid="search-input"
            className="h-9 w-full rounded-[10px] bg-tf-surface-1 pl-9 pr-9 text-[14px] text-tf-text-1 focus:outline-none focus:ring-2 focus:ring-tf-blue/30 placeholder:text-tf-text-3"
          />
          {q && (
            <button onClick={() => setQ('')} className="absolute right-2 top-1/2 -translate-y-1/2 flex h-6 w-6 items-center justify-center rounded-full bg-[#8E8E93] text-white haptic">
              <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="3"><path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" /></svg>
            </button>
          )}
        </div>
        <button onClick={() => nav(-1)} className="text-[14px] text-tf-blue">Cancel</button>
      </div>

      {/* Recent (only when not searching) */}
      {!q && (
        <>
          <h2 className="mt-4 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Recent</h2>
          <div className="mx-4 ios26-list bg-white">
            {RECENT.map((r) => (
              <button key={r.q} onClick={() => setQ(r.q)} className="flex w-full items-center gap-3 border-b border-tf-border last:border-b-0 px-4 py-3 active:bg-tf-surface-1 transition" data-testid={`recent-${r.q}`}>
                <svg viewBox="0 0 24 24" className="h-4 w-4 text-tf-text-3" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" strokeLinecap="round" /></svg>
                <span className="text-[14px] text-tf-text-1">{r.q}</span>
                <span className="ml-auto text-[11px] text-tf-text-3">{r.time}</span>
              </button>
            ))}
          </div>

          <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Suggested</h2>
          <div className="mx-4 flex flex-wrap gap-1.5">
            {SUGGESTED.map((s) => (
              <button key={s} onClick={() => setQ(s)} className="rounded-full bg-white border border-tf-border px-3 py-1.5 text-[13px] font-medium text-tf-text-1 active:opacity-40" data-testid={`suggested-${s}`}>
                {s}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Results */}
      {q && (
        <>
          <h2 className="mt-4 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">{filtered.length} results for "{q}"</h2>
          {filtered.length === 0 ? (
            <div className="mx-4 rounded-[16px] border border-tf-border bg-white p-6 text-center">
              <div className="text-[40px] mb-2">🔍</div>
              <div className="text-[14px] font-semibold text-tf-text-1">No results</div>
              <p className="mt-1 text-[12px] text-tf-text-3">Try a different search term</p>
            </div>
          ) : (
            <div className="mx-4 ios26-list bg-white">
              {filtered.map((r, i) => (
                <button
                  key={r.id}
                  onClick={() => nav(r.to)}
                  data-testid={`result-${r.id}`}
                  className={['flex w-full items-center gap-3 px-4 py-3 active:bg-tf-surface-1 transition', i < filtered.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-[10px] text-[18px]" style={{ backgroundColor: r.color + '18' }}>{r.icon}</div>
                  <div className="flex-1 text-left min-w-0">
                    <div className="text-[14px] font-semibold text-tf-text-1">{r.title}</div>
                    <div className="text-[11px] text-tf-text-3 truncate">{r.detail}</div>
                  </div>
                  <span className="rounded-full bg-tf-surface-1 px-1.5 py-0.5 text-[9px] font-bold uppercase text-tf-text-3">{r.type}</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
